import React, { useEffect, useMemo, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertTriangle, Plus, Trash2, ShieldAlert, FileEdit, Library } from 'lucide-react';
import { toast } from 'sonner';

const PROB = [1, 2, 3, 4, 5];
const IMPACT = [1, 2, 3, 4, 5];

const STATUSES = [
  { v: 'pending', label: 'Pending' },
  { v: 'approved', label: 'Approved' },
  { v: 'rejected', label: 'Rejected' },
];

import { makeFmt } from './currency';

export default function RiskAndChange({ calculation, onEvaluation }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [risks, setRisks] = useState([]);
  const [changes, setChanges] = useState([]);
  const [library, setLibrary] = useState([]);
  const [eval_, setEval_] = useState(null);

  const directCost = calculation?.total_direct_cost_usd || 0;
  const duration = calculation?.project_duration_months || 12;
  const sector = calculation?.sector;
  const city = calculation?.city;

  useEffect(() => {
    if (!sector) return;
    ConstructionAPI.getRiskLibrary(sector, city).then(d => setLibrary(d.risks || []));
  }, [sector, city]);

  const score = (r) => Number(r.probability) * Number(r.impact);

  const evalNow = async () => {
    try {
      const res = await ConstructionAPI.evaluateRisks({
        direct_cost_usd: directCost,
        duration_months: duration,
        risks, changes,
      });
      setEval_(res);
      onEvaluation?.(res);
    } catch (err) {
      toast.error('Risk evaluation failed');
    }
  };

  useEffect(() => { if (calculation) evalNow(); /* eslint-disable-next-line */ }, [risks, changes, calculation?.id]);

  const addRiskFromLibrary = (libRisk) => {
    setRisks(prev => [...prev, {
      title: libRisk.title,
      category: libRisk.category,
      probability: libRisk.default_probability,
      impact: libRisk.default_impact,
      cost_impact_usd: 0,
      schedule_impact_days: 0,
      mitigation: libRisk.typical_mitigation,
      _key: `${libRisk.key}-${Date.now()}`,
    }]);
    toast.success(`Added "${libRisk.title}"`);
  };

  const addBlankRisk = () => {
    setRisks(prev => [...prev, {
      title: 'New risk', category: 'commercial',
      probability: 3, impact: 3, cost_impact_usd: 0, schedule_impact_days: 0, mitigation: '',
      _key: `new-${Date.now()}`,
    }]);
  };

  const updateRisk = (idx, field, value) => setRisks(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeRisk = (idx) => setRisks(prev => prev.filter((_, i) => i !== idx));

  const addChange = () => setChanges(prev => [...prev, {
    title: 'New change order', cost_delta_usd: 0, schedule_delta_days: 0, status: 'pending', _key: `c-${Date.now()}`,
  }]);
  const updateChange = (idx, field, value) => setChanges(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  const removeChange = (idx) => setChanges(prev => prev.filter((_, i) => i !== idx));

  const heatColor = (score) => {
    if (score >= 16) return 'bg-red-500 text-white';
    if (score >= 12) return 'bg-orange-500 text-white';
    if (score >= 8) return 'bg-yellow-400 text-slate-900';
    return 'bg-emerald-500 text-white';
  };

  const totalRiskBudget = useMemo(() => {
    const directBase = directCost || 0;
    const adjusted = directBase + (eval_?.total_risk_change_cost_usd || 0);
    return { adjusted, delta: eval_?.total_risk_change_cost_usd || 0 };
  }, [directCost, eval_]);

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to add risks and change orders.</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="risk-change-tab">
      {/* Header summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Pill label="Risks" value={`${eval_?.risk_count ?? 0}`} accent="bg-rose-600" icon={<ShieldAlert className="h-4 w-4" />} />
        <Pill label="High-priority" value={`${eval_?.high_risk_count ?? 0}`} accent="bg-red-600" icon={<AlertTriangle className="h-4 w-4" />} />
        <Pill label="Expected risk cost" value={fmt$(eval_?.expected_risk_cost_usd)} accent="bg-amber-600" icon={<ShieldAlert className="h-4 w-4" />} />
        <Pill label="Approved changes" value={fmt$(eval_?.approved_change_cost_usd)} accent="bg-emerald-600" icon={<FileEdit className="h-4 w-4" />} />
        <Pill label="Adj. direct cost" value={fmt$(totalRiskBudget.adjusted)} accent="bg-slate-900" icon={<ShieldAlert className="h-4 w-4" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Risk register */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base"><ShieldAlert className="inline h-4 w-4 mr-1.5" />Risk Register</CardTitle>
              <Button size="sm" onClick={addBlankRisk} data-testid="risk-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add risk</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Title</th>
                    <th className="text-center p-2">P</th>
                    <th className="text-center p-2">I</th>
                    <th className="text-center p-2">Score</th>
                    <th className="text-right p-2">Cost $</th>
                    <th className="text-right p-2">Days</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {risks.map((r, i) => {
                    const s = score(r);
                    return (
                      <tr key={r._key} className="border-t">
                        <td className="p-1"><Input className="h-8 text-sm" value={r.title} onChange={e => updateRisk(i, 'title', e.target.value)} /></td>
                        <td className="p-1 w-16">
                          <Select value={String(r.probability)} onValueChange={v => updateRisk(i, 'probability', Number(v))}>
                            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{PROB.map(p => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-1 w-16">
                          <Select value={String(r.impact)} onValueChange={v => updateRisk(i, 'impact', Number(v))}>
                            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{IMPACT.map(p => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-2 text-center"><Badge className={`${heatColor(s)} font-bold`}>{s}</Badge></td>
                        <td className="p-1 w-28"><Input className="h-8 text-right text-sm" type="number" value={r.cost_impact_usd} onChange={e => updateRisk(i, 'cost_impact_usd', Number(e.target.value))} /></td>
                        <td className="p-1 w-20"><Input className="h-8 text-right text-sm" type="number" value={r.schedule_impact_days} onChange={e => updateRisk(i, 'schedule_impact_days', Number(e.target.value))} /></td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRisk(i)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button></td>
                      </tr>
                    );
                  })}
                  {risks.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">No risks. Add manually or pick from the Library →</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Risk library */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base"><Library className="inline h-4 w-4 mr-1.5" />Risk Library</CardTitle>
            <p className="text-xs text-slate-500">Curated common risks for {sector}.</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[460px] overflow-y-auto divide-y">
              {library.map(r => (
                <div key={r.key} className="p-3 hover:bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => addRiskFromLibrary(r)} data-testid={`risk-lib-add-${r.key}`}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs capitalize">{r.category.replace('_',' ')}</Badge>
                    <Badge className={`${heatColor(r.default_probability * r.default_impact)} text-xs`}>P{r.default_probability}·I{r.default_impact}</Badge>
                    {r.origin === 'city' && <Badge className="bg-purple-600 text-white text-[10px]">CITY · {r.city}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change orders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base"><FileEdit className="inline h-4 w-4 mr-1.5" />Change Orders</CardTitle>
            <Button size="sm" onClick={addChange} data-testid="change-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add change order</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Title</th>
                  <th className="text-right p-2">Cost Δ</th>
                  <th className="text-right p-2">Schedule Δ days</th>
                  <th className="text-left p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={c._key} className="border-t">
                    <td className="p-1"><Input className="h-8 text-sm" value={c.title} onChange={e => updateChange(i, 'title', e.target.value)} /></td>
                    <td className="p-1 w-32"><Input className="h-8 text-right text-sm" type="number" value={c.cost_delta_usd} onChange={e => updateChange(i, 'cost_delta_usd', Number(e.target.value))} /></td>
                    <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={c.schedule_delta_days} onChange={e => updateChange(i, 'schedule_delta_days', Number(e.target.value))} /></td>
                    <td className="p-1 w-32">
                      <Select value={c.status} onValueChange={v => updateChange(i, 'status', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeChange(i)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button></td>
                  </tr>
                ))}
                {changes.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-sm">No change orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Pill({ label, value, accent, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}
